import React, { useEffect, useRef, useState } from 'react';
import PageContainer from 'src/components/container/PageContainer';
import DashboardCard from 'src/components/shared/DashboardCard';
import { Button, Grid, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Box } from '@mui/system';
import TextInputs from 'src/components/inputs/TextInputs';
import ClinetSuggestion from './ClinetSuggestion';
import { useMain } from '../hooks/useMain';
import RadioInputs from 'src/components/inputs/RadioInputs';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import SelectInputs from 'src/components/inputs/SelectInputs';
import NewSelectInput from './NewSelectInput';
import ServiceSuggestion from '../Billings/ServiceSuggestion';
import { IoStar } from "react-icons/io5";
import TimeSlotSelector from 'src/components/inputs/TimeSlotSelector';
import { useNavigate } from 'react-router';
import ServiceSuggestionAppointment from './ServiceSuggestionAppointment';
import { useSearchParams } from 'react-router-dom';

const initialValue = {
  clientName: '',
  clientNumber: '',
  dateOfAppointment: getCurrentDateInFormat(),
  sourceOfAppointment: 'Walk In',
  timeOfAppointment: '',
  gender: '',
  serviceSelected: [
    {
      service: '',
      serviceName: '',
      serviceProvider: '',
    },
  ],
};

const initialFormValidation = {
  clientNameValidation: false,
  clientNumberValidation: false,
  clientGenderValidation: false,
  dateOfAppointmentValidation: false,
  timeOfAppointmentValidation: false,
  serviceSelectedValidation: [
    {
      serviceValidation: false,
      serviceProviderValidation: false,
    },
  ],
};

const sourceOptions = [
  { title: 'Walk In', value: 'Walk In' },
  { title: 'Existing', value: 'Existing' },
  { title: 'Phone Call', value: 'Phone Call' },
  { title: 'Facebook', value: 'Facebook' },
];

const AddAppointment = () => {
  const { createAppointment, activeBranchId, getBranch, setShowMessage, getBranchTimings } = useMain();

  const [formData, setFormData] = useState(initialValue);
  const [formValidation, setFormValidation] = useState(initialFormValidation);
  const [servicesCat, setServicesCat] = useState([]);
  const [genderWiseService, setGenderWiseService] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [client, setClient] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [activeInputField, setActiveInputField] = useState('');
  const [loading, setLoader] = useState(false);
  const [focusedServiceIndex, setFocusedServiceIndex] = useState(null);
  const [serviceSuggested, setServiceSuggested] = useState([]);
  const clientNameValidation = !formData.clientName.trim();
  const clientNumberValidation = !formData.clientNumber || formData.clientNumber.length !== 10;
  const clientGenderValidation = !formData.gender;
  const dateOfAppointmentValidation = !formData.dateOfAppointment;
  const timeOfAppointmentValidation = !formData.timeOfAppointment;
  const [branchTimings, setBranchTimings] = useState({salonOpeningTime: '', salonClosingTime: '',});
  const serviceSelectedValidation = formData.serviceSelected.map((item) => ({
    serviceValidation: !item.service.trim(),
    serviceProviderValidation: item.serviceProvider === '',
  }));

  const [sourceOfAppointment, setSourceOfAppointment] = useState(sourceOptions[0].value);
  const navigate = useNavigate()
    const [searchParams] = useSearchParams();
    const clientNumberInputRef = useRef(null);
    const serviceInputRefs = useRef([]);

   useEffect(() => {
     // Small timeout to ensure the component is fully rendered
     const timer = setTimeout(() => {
       if (clientNumberInputRef.current) {
         clientNumberInputRef.current.focus();
       }
     }, 100);
     
     return () => clearTimeout(timer);
   }, []);

  useEffect(() => {
  const datetimeParam = searchParams.get('datetime');
  const staffIdParam = searchParams.get('staffId');

  if (datetimeParam) {
    try {
        let newFormData = {};
      if (datetimeParam.includes('T')) {
        const [datePart, timePart] = datetimeParam.split('T');
        const [time] = timePart.split('.');

          newFormData = {
          dateOfAppointment: datePart,
          timeOfAppointment: time.substring(0, 5),
          // ✅ Reset serviceSelected on calendar navigation
          serviceSelected: [
              { service: '', serviceName: '', serviceProvider: staffIdParam || '' }
          ],
        };
      } else {
         newFormData = {
          dateOfAppointment: datetimeParam,
          timeOfAppointment: getCurrentTime(),
          // ✅ Reset serviceSelected here too
          serviceSelected: [
            { service: '', serviceName: '', serviceProvider: '' }
          ],
        };
      }
      setFormData(prev => ({
          ...prev,
          ...newFormData,
        }));

      setFormValidation(prev => ({
        ...prev,
        dateOfAppointmentValidation: false,
        timeOfAppointmentValidation: false,
        serviceSelectedValidation: [
          { serviceValidation: false, serviceProviderValidation: false }
        ],
      }));
    } catch (error) {
      console.error('Error parsing datetime parameter:', error);
    }
  } else {
    // Direct feature navigation → no datetime param → use clean defaults
    setFormData({
      ...initialValue,
      dateOfAppointment: getCurrentDateInFormat(),
      timeOfAppointment: '',
      sourceOfAppointment: 'Walk In',
      serviceSelected: [
        { service: '', serviceName: '', serviceProvider: '' }
      ],
    });
  }

  setServiceSuggested([]);
  setFocusedServiceIndex(null);
}, [searchParams]);
useEffect(() => {
  const fetchTimings = async () => {
    if (activeBranchId) {
      const res = await getBranchTimings(activeBranchId);
      if (res?.statusCode === 200 && res?.data) {
        setBranchTimings({
          salonOpeningTime: res.data.salonOpeningTime || '',
          salonClosingTime: res.data.salonClosingTime || '',
        });
      }
    }
  };
  fetchTimings();
}, [activeBranchId]);

useEffect(() => {
  // Only set default values if no datetime parameter is present
  const datetimeParam = searchParams.get('datetime');
  if (!datetimeParam) {
    setFormData({
      ...initialValue,
      dateOfAppointment: getCurrentDateInFormat(),
      timeOfAppointment: '',
      sourceOfAppointment: 'Walk In',
      serviceSelected: [
        {
          service: '',
          serviceName: '',
          serviceProvider: '',
        },
      ],
    });
  }

  setFormValidation({
    ...initialFormValidation,
    serviceSelectedValidation: [
      {
        serviceValidation: false,
        serviceProviderValidation: false,
      },
    ],
  });

  setServiceSuggested([]);
  setFocusedServiceIndex(null);
}, []); // Remove searchParams from dependency array to avoid infinite loops


  const handleSourceChange = (value) => {
    setSourceOfAppointment(value);
    setFormData((prev) => ({ ...prev, sourceOfAppointment: value }));
  };

  const updateServiceSuggested = (inputValue) => {
    if (inputValue === '') {
      setServiceSuggested([]);
      setFocusedServiceIndex(null);  // ✅ Hide suggestion box
    } else {
      const serviceSuggestions = genderWiseService?.filter((service) =>
        service.serviceName.toLowerCase().includes(inputValue.toLowerCase())
      );
      setServiceSuggested(serviceSuggestions);
    }
  };

  const handleServiceInputChange = (e, index) => {
    const { name, value } = e.target;
    if (name === 'service') {
      const updatedServiceSelected = [...formData.serviceSelected];
      updatedServiceSelected[index]["service"] = "";
      updatedServiceSelected[index]["serviceName"] = value;
      setFormData((prev) => ({ ...prev, serviceSelected: updatedServiceSelected }));
      updateServiceSuggested(value);
      setFocusedServiceIndex(index);
    }
  };
  console.log(formData);

    const handlePhonePaste = (e) => {
      e.preventDefault();
      const digits = e.clipboardData.getData('text').replace(/\D/g, '');
      const value = digits.slice(-10); // keep the LAST 10 digits
      setFormData((prev) => ({ ...prev, clientNumber: value }));
      updateSuggested('clientNumber', value);
    };

  // const handleChange = (e) => {
  //   const { name, value } = e.target;
  //   setFormData((prev) => ({ ...prev, [name]: value }));
  //   if (name === 'clientNumber' || name === 'clientName') {
  //     updateSuggested(name, value);
  //   }
  // };
  const handleChange = (e) => {
    const { name } = e.target;
      let { value } = e.target;

      // For phone number: keep only digits, and if more than 10, keep the LAST 10
    if (name === 'clientNumber') {
      value = value.replace(/\D/g, '');
      if (value.length > 10) {
        value = value.slice(-10);
      }
    }
  setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'clientNumber' || name === 'clientName') {
      updateSuggested(name, value);
    }
  };

  const updateSuggested = (fieldName, inputValue) => {
    if (inputValue === '') {
      setSuggested([]);
    } else {
      const suggestions = client?.filter((client) =>
        client[fieldName]?.toLowerCase().includes(inputValue.toLowerCase())
      );
      setSuggested(suggestions);
    }
  };

  const handleSuggestedClick = (client) => {
    const { clientNumber, clientName, gender } = client;
    setFormData((prev) => ({ ...prev, clientNumber, clientName, gender }));
    setSuggested([]);
  };

  const addRow = () => {
    const isUnfilledField = formData.serviceSelected.some(
      (item) => item.serviceProvider === '' || item.service === ''
    );
    if (isUnfilledField)
      return setShowMessage({
        message: 'Please select a staff and services',
        messageType: 'error',
      });
    setFormData((prev) => ({
      ...prev,
      serviceSelected: [...prev.serviceSelected, { service: '', serviceProvider: '' }],
    }));
       // Focus on the new service input after the state updates
    setTimeout(() => {
      const lastIndex = formData.serviceSelected.length;
      if (serviceInputRefs.current[lastIndex]) {
        serviceInputRefs.current[lastIndex].focus();
      }
    }, 100);
  };

  const removeRow = (index) => {
    const updatedServiceSelected = [...formData.serviceSelected];
    updatedServiceSelected.splice(index, 1);
    setFormData((prev) => ({ ...prev, serviceSelected: updatedServiceSelected }));
  };

  const handleServiceSelectedInputChange = (e, index) => {
    const { name, value } = e.target;
    const updatedServiceSelected = [...formData.serviceSelected];
    updatedServiceSelected[index][name] = value;
    setFormData((prev) => ({ ...prev, serviceSelected: updatedServiceSelected }));
  };

  const handleServiceSuggestedClick = (service) => {
    const index = focusedServiceIndex;
    const updatedServiceSelected = [...formData.serviceSelected];
    updatedServiceSelected[index]["service"] = service?._id;
    updatedServiceSelected[index]["serviceName"] = service?.serviceName;
    setFormData((prev) => ({ ...prev, serviceSelected: updatedServiceSelected }));
    setServiceSuggested([])
  };

  const handleSubmit = async () => {
    const {
      clientName,
      gender,
      clientNumber,
      timeOfAppointment,
      dateOfAppointment,
      serviceSelected,
    } = formData;

    const validation = {
      clientNameValidation: !clientName.trim(),
      clientNumberValidation: !clientNumber || clientNumber.length !== 10,
      clientGenderValidation: !gender,
      dateOfAppointmentValidation: !dateOfAppointment,
      timeOfAppointmentValidation: !timeOfAppointment,
      serviceSelectedValidation: serviceSelected.map((item) => ({
        serviceValidation: !item.service.trim(),
        serviceProviderValidation: item.serviceProvider === '',
      })),
    };

    setFormValidation((prev) => ({ ...prev, ...validation }));

    if (
      Object.values(validation).some((item) =>
        Array.isArray(item)
          ? item.some(
            (nestedItem) => nestedItem.serviceValidation || nestedItem.serviceProviderValidation,
          )
          : item,
      )
    )
      return;

    try {
      setLoader(true);
      const res = await createAppointment(
        JSON.stringify({ ...formData, branchDetails: activeBranchId }),
      );
      if (res.statusCode === 200) {
        setFormData({
          ...initialValue,
          dateOfAppointment: getCurrentDateInFormat(),
          timeOfAppointment: '',
          sourceOfAppointment: 'Walk In',
          serviceSelected: [
            {
              service: '',
              serviceName: '',
              serviceProvider: '',
            },
          ],
        });
        setFormValidation({
          ...initialFormValidation,
          serviceSelectedValidation: [
            {
              serviceValidation: false,
              serviceProviderValidation: false,
            },
          ],
        });
        setShowMessage({
          message: res?.message || 'Appointment created successfully',
          messageType: 'success',
        });
        navigate('/dashboard');
      } else {
        setShowMessage({
          message: res?.message || 'Error occurs in creating appointment',
          messageType: 'error',
        });
      }
    } catch (error) {
      setShowMessage({
        message: error?.message || 'Error occurs in creating appointment',
        messageType: 'error',
      });
    } finally {
      setLoader(false);
    }
  };


  // const handleSubmit = async () => {
  //   const {
  //     clientName,
  //     gender,
  //     clientNumber,
  //     timeOfAppointment,
  //     dateOfAppointment,
  //     serviceSelected,
  //   } = formData;

  //   const validation = {
  //     clientNameValidation: !clientName.trim(),
  //     clientNumberValidation: !clientNumber || clientNumber.length !== 10,
  //     clientGenderValidation: !gender,
  //     dateOfAppointmentValidation: !dateOfAppointment,
  //     timeOfAppointmentValidation: !timeOfAppointment,
  //     serviceSelectedValidation: serviceSelected.map((item) => ({
  //       serviceValidation: !item.service.trim(),
  //       serviceProviderValidation: item.serviceProvider === '',
  //     })),
  //   };

  //   setFormValidation((prev) => ({ ...prev, ...validation }));

  //   if (
  //     Object.values(validation).some((item) =>
  //       Array.isArray(item)
  //         ? item.some(
  //           (nestedItem) => nestedItem.serviceValidation || nestedItem.serviceProviderValidation
  //         )
  //         : item
  //     )
  //   )
  //     return;

  //   try {
  //     setLoader(true);
  //     const res = await createAppointment(
  //       JSON.stringify({ ...formData, branchDetails: activeBranchId })
  //     );
  //     if (res.statusCode === 200) {
  //       setFormData({
  //         ...initialValue,
  //         dateOfAppointment: getCurrentDateInFormat(),
  //         timeOfAppointment: '',
  //         sourceOfAppointment: 'Walk In',
  //         serviceSelected: [
  //           {
  //             service: '',
  //             serviceName: "",
  //             serviceProvider: '',
  //           },
  //         ],
  //       });
  //       setFormValidation({
  //         ...initialFormValidation,
  //         serviceSelectedValidation: [
  //           {
  //             serviceValidation: false,
  //             serviceProviderValidation: false,
  //           },
  //         ],
  //       });
  //       setShowMessage({
  //         message: res?.message || 'Appointment created successfully',
  //         messageType: 'success',
  //       });
  //     } else {
  //       setShowMessage({
  //         message: res?.message || 'Error occurs in creating appointment',
  //         messageType: 'error',
  //       });
  //     }
  //   } catch (error) {
  //     setShowMessage({
  //       message: error?.message || 'Error occurs in creating appointment',
  //       messageType: 'error',
  //     });
  //   } finally {
  //     setLoader(false);
  //   }
  // };

  const handleInputFocus = () => {
    setSuggested([]);
    const activeElement = document.activeElement;
    if (activeElement && activeElement.getAttribute('name')) {
      setActiveInputField(activeElement.getAttribute('name'));
    } else {
      setActiveInputField('');
    }
  };

  useEffect(() => {
    document.addEventListener('focus', handleInputFocus, true);
    return () => {
      document.removeEventListener('focus', handleInputFocus, true);
    };
  }, [activeInputField, Object.keys(client).length]);

  const getAllData = async () => {
    try {
      const res = await getBranch(activeBranchId);
      setClient(res?.data?.client || []);
      setStaff(res?.data?.staffs || []);
      setServices(res?.data?.services || []);
      setGenderWiseService(res?.data?.services || []);
    } catch (error) {
    }
  };
  useEffect(() => {
    if (activeBranchId) {
      getAllData()
    }

    return () => {
      setClient([]);
      setStaff([]);
      setServices([]);
    };
  }, [activeBranchId]);

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // This logic disables the mouse wheel event
  useEffect(() => {
    const handleWheel = (e) => {
      if (document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };

    window.addEventListener('wheel', handleWheel);
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Extract selectedDate from formData and compare with today's date
  const selectedDate = new Date(formData.dateOfAppointment); // Ensure it's a Date object
  const isToday = new Date().toDateString() === selectedDate.toDateString();

  return (
    <PageContainer title="Book Appointment" description="This is add Appointment">
      <DashboardCard title="Book Appointment">
        <Box style={{ marginTop: '20px' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextInputs
                title={'Phone Number'}
                name={'clientNumber'}
                ref={clientNumberInputRef}
                value={formData?.clientNumber}
                 onPaste={handlePhonePaste}   
                handleChange={handleChange}
                type={'text'}
                placeholder={'87******22'}
                fieldValidation={clientNumberValidation && formValidation.clientNumberValidation}
              />
              {suggested.length > 0 && activeInputField === 'clientNumber' && (
                <ClinetSuggestion data={suggested} handleSuggestedClick={handleSuggestedClick} />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <TextInputs
                title={'Client Name'}
                name={'clientName'}
                value={formData?.clientName}
                handleChange={handleChange}
                type={'text'}
                placeholder={'Client Name'}
                fieldValidation={clientNameValidation && formValidation.clientNameValidation}
              />
              {suggested.length > 0 && activeInputField === 'clientName' && (
                <ClinetSuggestion data={suggested} handleSuggestedClick={handleSuggestedClick} />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <RadioInputs
                title={'Gender'}
                name={'gender'}
                value={formData?.gender}
                handleChange={handleChange}
                options={[
                  { title: 'M', value: 'Male' },
                  { title: 'F', value: 'Female' },
                ]}
                fieldValidation={clientGenderValidation && formValidation.clientGenderValidation}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Date, Time, and Source of Appointment Fields in One Row */}
        <Box style={{ marginTop: '20px' }}>
          <Grid container spacing={3}>
            {/* Combined Date, Time, and Source container */}
            <Grid item xs={12} md={4} style={{ paddingRight: '10px' }}>
              <div
                style={{
                  display: 'flex',
                  // border: '1px solid #ccc',
                  borderRadius: '5px',
                  padding: '8px',
                  backgroundColor: '#fff',
                  width: '100%',
                }}
              >
                {/* Date of Appointment Input */}
                <TextInputs
                  title={'Date'}
                  name={'dateOfAppointment'}
                  value={formData?.dateOfAppointment}
                  handleChange={handleChange}
                  type={'date'}
                  min={new Date().toISOString().split('T')[0]} // Prevent past date selection
                  fieldValidation={dateOfAppointmentValidation && formValidation.dateOfAppointmentValidation}
                  style={{
                    width: '50%', // Taking 50% of space
                    border: 'none', // Remove default border
                    padding: '0 8px',
                    backgroundColor: 'transparent', // Transparent background to match unified look
                    borderRight: '1px solid #ccc', // Add divider between date and time
                  }}
                />

                {/* Time of Appointment Input */}
                 <TimeSlotSelector
                  title={'Time'}
                  name={'timeOfAppointment'}
                  value={formData?.timeOfAppointment}
                  handleChange={handleChange}
                  fieldValidation={timeOfAppointmentValidation && formValidation.timeOfAppointmentValidation}
                  required={true}
                  isToday={isToday}
                  branchTimings={branchTimings}
                  style={{
                    width: '50%',
                    border: 'none',
                    padding: '0 8px',
                    backgroundColor: 'transparent',
                  }}
                />
              </div>
            </Grid>

            <Grid item xs={12} md={4}>
              {/* <SelectInputs
                name={'sourceOfAppointment'}
                value={formData.sourceOfAppointment}
                title={'Source of Appointment'}
                required={false}
                handleChange={handleChange}
                options={sourceOptions}
                optionTitle={'title'}
                optionValue={'value'}
              /> */}
              <div style={{ marginTop: '20px', marginBottom: '8px', fontWeight: 'bold' }}>
                Source of Appointment
              </div>
              <Box sx={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
                {sourceOptions.map((option) => (
                  <ToggleButton
                    key={option.value}
                    value={option.value}
                    selected={sourceOfAppointment === option.value}
                    onChange={() => handleSourceChange(option.value)}
                    aria-label={option.title}
                    sx={{
                      flexGrow: 1,
                      minWidth: {
                        xs: '15%',   // full width for mobile
                        sm: 'auto',   // auto for small screens
                        md: '100px',  // fixed/increased width on tablets
                      },
                      color: '#000',
                      // height: '3.3rem',
                      height: '2rem',
                      fontSize: '0.8rem',
                      backgroundColor: sourceOfAppointment === option.value ? '#6174DD' : '#f0f0f0',
                      '&.Mui-selected': {
                        backgroundColor: '#6174DD',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: '#6174DD',
                          color: '#fff',
                        },
                      },
                      '&:hover': {
                        backgroundColor: '#d0d0d0',
                        color: '#000',
                      },
                    }}
                  >
                    {option.title}
                  </ToggleButton>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
        {formData.serviceSelected.map((item, index) => (
          <Box style={{ marginTop: '20px' }} key={index}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <label>Services<IoStar color='red' style={{ height: '6px', fontWeight: 'bold', marginBottom: '10px' }} /></label>
                <input
                  type="text"
                  ref={(el) => (serviceInputRefs.current[index] = el)}
                  className={`form-control ${item?.serviceName === '' ? 'border border-2'
                    : ''
                    }`}
                  placeholder="Service"
                  name="service"
                  value={item?.serviceName}
                  onChange={(e) => handleServiceInputChange(e, index)}
                  autoComplete='off'
                  style={{
                    padding: '14px',
                    border: '1px solid #dee2e6',
                    outline: isFocused ? '2px solid #6174DD' : 'none', // Focus style
                  }}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                />
                 {serviceSelectedValidation[index]?.serviceValidation &&
                  formValidation.serviceSelectedValidation[index]?.serviceValidation && (
                    <div className="text-danger">Service Name is required</div>
                  )}

                   {serviceSuggested.length > 0 && focusedServiceIndex === index && (
                  <ServiceSuggestionAppointment
                    data={serviceSuggested}
                    handleServiceSuggestedClick={handleServiceSuggestedClick}
                    inputRef={serviceInputRefs.current[index]}
                    setServiceSuggested={setServiceSuggested}
                    focusedServiceIndex={focusedServiceIndex}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <SelectInputs
                  name={'serviceProvider'}
                  value={item?.serviceProvider}
                  title={'Assign to'}
                  handleChange={(e) => handleServiceSelectedInputChange(e, index)}
                  options={staff}
                  optionValue={'_id'}
                  optionTitle={'name'}
                  fieldValidation={
                    serviceSelectedValidation[index]?.serviceProviderValidation &&
                    formValidation.serviceSelectedValidation[index]?.serviceProviderValidation
                  }
                />
              </Grid>
              <Grid item xs={12} md={4} style={{ display: 'flex', alignItems: 'center' }}>
                <Grid item xs={12} md={4} mt={'10px'}>
                  <Button
                    variant="contained"
                    color="primary"
                    target="_blank"
                    sx={{
                      background: '#6174DD',
                      width: '50px',
                      height: '50px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      marginTop: '1rem',
                    }}
                    onClick={addRow}
                  >
                    <Typography
                      variant="h5"
                      component="span"
                      sx={{
                        color: "white",
                        position: "absolute",
                        transition: "opacity 0.3s ease",
                        opacity: 1,
                        fontSize: '22px',
                        ".MuiButton-root:hover &": {
                          opacity: 0,
                        },
                      }}
                    >
                      +
                    </Typography>
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{
                        color: "white",
                        position: "absolute",
                        opacity: 0,
                        fontSize: '18px',
                        transition: "opacity 0.3s ease",
                        ".MuiButton-root:hover &": {
                          opacity: 1,
                        },
                      }}
                    >
                      Add More
                    </Typography>
                  </Button>
                </Grid>
                {index !== 0 ? (
                  <Grid item xs={12} md={4} mt={'10px'}>
                    <Button
                      variant="contained"
                      color="primary"
                      target="_blank"
                      sx={{
                        background: '#ff0000',
                        width: '50px',
                        height: '50px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        marginTop: '1rem',
                        '&:hover': {
                          background: '#ff6666', // Change the background color on hover
                        },
                      }}
                      onClick={() => removeRow(index)}
                    >
                      {/* Display "-" symbol */}
                      <Typography
                        variant="h5"
                        component="span"
                        sx={{
                          color: 'white',
                          position: 'absolute',
                          fontSize: '22px',
                          transition: 'opacity 0.3s ease',
                          opacity: 1,
                          ".MuiButton-root:hover &": {
                            opacity: 0,
                          },
                        }}
                      >
                        -
                      </Typography>

                      {/* Display "Remove" text, initially invisible */}
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          color: 'white',
                          position: 'absolute',
                          fontSize: '16px',
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                          ".MuiButton-root:hover &": {
                            opacity: 1,
                          },
                        }}
                      >
                        Remove
                      </Typography>
                    </Button>
                  </Grid>
                ) : (
                  ''
                )}
              </Grid>
            </Grid>
          </Box>
        ))}
        {loading ? (
          <ShowLoader value={loading} />
        ) : (
          <div style={{ width: '200px', margin: '3rem auto' }}>
            <Button
              variant="contained"
              fullWidth
              color="primary"
              target="_blank"
              sx={{
                background: '#6174DD',
              }}
              onClick={handleSubmit}
            >
              Book Appointment
            </Button>
          </div>
        )}
      </DashboardCard>
    </PageContainer>
  );
};

export default AddAppointment;

// Function to get the current date in a specific format
function getCurrentDateInFormat() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Function to get the current time in a specific format
function getCurrentTime() {
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  return `${formattedHours}:${formattedMinutes}`;
}